import { Layout, Button, Grid } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useAppSelector } from '@store/store';
import { Outlet } from 'react-router-dom';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { UserMenu } from '@pages/cmp/nav/UserMenu';
import { CartMenu } from '@eshop/pages/cart/CartMenu.tsx';
// import TopBar from '@features/dashboard/components/TopBar.tsx';
import CommandPalette from '@features/dashboard/components/CommandPalette.tsx';
import {
  useDashboardBreadcrumbs,
  type UseDashboardBreadcrumbsOptions,
} from '@features/dashboard/hooks/useDashboardBreadcrumbs.ts';
import { useCommandPalette } from '@features/dashboard/hooks/useCommandPalette.ts';
import ThemeToggle from './ThemeToggle';
import CompactMobileHeader from './CompactMobileHeader.tsx';
import AdminLayoutPageHeadingContext from './AdminLayoutPageHeadingContext.tsx';
import { selectSiteSettings, selectSiteTitle } from '@store/slices/settingsSlice.tsx';
import { useUnifiedDocumentTitle } from './useUnifiedDocumentTitle.ts';
import type { DashboardNavGroup } from '@features/dashboard/registry/types.ts';

import './AdminLayout.css';

const { Header, Content, Footer } = Layout;

export interface AdminLayoutMobileMenuContext {
  currentPageTitle: string | null;
  openCommandPalette: () => void;
  searchButtonLabel: string;
  searchButtonAriaLabel: string;
}

interface AdminLayoutProps {
  children?: ReactNode;
  breadcrumbOptions?: UseDashboardBreadcrumbsOptions;
  commandPaletteGroup?: DashboardNavGroup;
  searchButtonLabel?: string;
  searchButtonAriaLabel?: string;
  showCart?: boolean;
  mobileHeaderMode?: 'default' | 'compact';
  // showTopBar?: boolean;
  sideBar?: ReactNode;
  sideMobileMenu?: ReactNode | ((context: AdminLayoutMobileMenuContext) => ReactNode);
}

const AdminLayout: React.FC<AdminLayoutProps> = ({
  children,
  breadcrumbOptions,
  commandPaletteGroup,
  searchButtonLabel,
  searchButtonAriaLabel,
  showCart = true,
  mobileHeaderMode = 'default',
  // showTopBar = true,
  sideBar,
  sideMobileMenu,
}) => {
  const { items: breadcrumbs /* isLoading: breadcrumbsLoading */ } = useDashboardBreadcrumbs(breadcrumbOptions);
  const commandPalette = useCommandPalette();
  const siteTitle = useAppSelector(selectSiteTitle);
  const siteSettings = useAppSelector(selectSiteSettings);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const isCompactMobileHeader = isMobile && mobileHeaderMode === 'compact';

  const showSidebar = !!screens.md && !!sideBar;

  const currentPageTitle = breadcrumbs.length
    ? breadcrumbs[breadcrumbs.length - 1]?.label ?? null
    : null;

  const documentTitleOptions = useMemo(() => ({ defaultPageTitle: 'Panel' }), []);
  useUnifiedDocumentTitle(currentPageTitle, siteTitle, documentTitleOptions);

  const computedSearchLabel = searchButtonLabel ?? 'Buscar (Ctrl+K)';
  const computedSearchAriaLabel = searchButtonAriaLabel ?? 'Abrir paleta de comandos (Ctrl+K)';

  const handleSearchClick = () => {
    if (commandPaletteGroup) {
      commandPalette.open({ group: commandPaletteGroup });
      return;
    }
    commandPalette.open();
  };

  const resolvedSideMobileMenu = useMemo(() => {
    if (typeof sideMobileMenu === 'function') {
      return sideMobileMenu({
        currentPageTitle,
        openCommandPalette: handleSearchClick,
        searchButtonLabel: computedSearchLabel,
        searchButtonAriaLabel: computedSearchAriaLabel,
      });
    }

    return sideMobileMenu;
  }, [
    computedSearchAriaLabel,
    computedSearchLabel,
    currentPageTitle,
    handleSearchClick,
    sideMobileMenu,
  ]);

  const hasSidebar = !!sideBar;
  const businessName = siteSettings?.name ?? siteTitle;
  const logoUrl = siteSettings?.logo?.thumbnails?.default;
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const [failedDashBannerUrl, setFailedDashBannerUrl] = useState<string | null>(null);
  const resolvedLogoUrl = logoUrl && logoUrl !== failedLogoUrl ? logoUrl : null;
  const pageHeadingContextValue = useMemo(
    () => ({
      currentPageTitle,
      isHeaderTitleVisible: isCompactMobileHeader,
    }),
    [currentPageTitle, isCompactMobileHeader],
  );
  const showBanner = businessName || resolvedLogoUrl;
  const dashBanner = siteSettings?.dash_banner?.thumbnails?.urls.large;
  const resolvedDashBanner = dashBanner && dashBanner !== failedDashBannerUrl ? dashBanner : null;
  const hasDashBanner = !!resolvedDashBanner;
  const styleBanner = {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'start',
    gap: 16,
    padding: '5px 8px',
    minHeight: '64px',
    aspectRatio: '7 / 1',
  };

  const renderBanner = () => {
    if (!showBanner) return null;

    return (
      <div
        className={[
          'banner-header-container',
          hasDashBanner ? 'contenedor' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={
          hasDashBanner
            ? { ...styleBanner, width: '100%', minWidth: '400px' }
            : styleBanner
        }
      >
        {hasDashBanner ? (
          <img
            src={resolvedDashBanner}
            alt={businessName ?? 'Logo'}
            className="contenido img_cover"
            style={{ borderRadius: 20 }}
            onError={() => setFailedDashBannerUrl(dashBanner ?? null)}
          />
        ) : (
          <>
            {resolvedLogoUrl ? (
              <img
                src={resolvedLogoUrl}
                alt={businessName ?? 'Logo'}
                className="erp-layout-header__brand-logo"
                onError={() => setFailedLogoUrl(logoUrl ?? null)}
              />
            ) : null}
            {businessName ? (
              <h1
                style={{
                  margin: 0,
                  fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                  fontWeight: 700,
                  zIndex: 1,
                  color: 'var(--text-default)',
                }}
              >
                {businessName}
              </h1>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const searchButton = (
    <Button
      type="default"
      icon={<SearchOutlined />}
      onClick={handleSearchClick}
      aria-label={computedSearchAriaLabel}
      className="erp-layout-header__search"
    >
      {computedSearchLabel}
    </Button>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: 'var(--surface-app)' }}>
      {showSidebar && hasSidebar ? sideBar : null}

      <Layout style={{ height: '100vh', overflowY: 'auto', background: 'var(--surface-app)' }}>
        <Header className={['erp-layout-header', isCompactMobileHeader ? 'erp-layout-header--compact' : ''].filter(Boolean).join(' ')}>
          {isCompactMobileHeader ? (
            <CompactMobileHeader
              businessName={businessName ?? siteTitle}
              currentPageTitle={currentPageTitle}
              leading={resolvedSideMobileMenu}
              logoUrl={resolvedLogoUrl}
              trailing={
                <>
                  {showCart ? <CartMenu /> : null}
                  <UserMenu />
                </>
              }
            />
          ) : (
            <>
              <div className="inner-header">
                {renderBanner()}
                {searchButton}
                <ThemeToggle size="small" />
                {showCart ? <CartMenu /> : null}
                <UserMenu />
              </div>
              {resolvedSideMobileMenu ? <div className="side-mobile-menu">{resolvedSideMobileMenu}</div> : null}
            </>
          )}
        </Header>

        <AdminLayoutPageHeadingContext.Provider value={pageHeadingContextValue}>
          <CommandPalette controller={commandPalette} />

          {/*<Content style={{ margin: 16, minHeight: 'auto', background: 'transparent' }}>{children ?? <Outlet />}</Content>*/}
          <Content className={"erp-layout-content"}>{children ?? <Outlet />}</Content>

          <Footer
            style={{
              textAlign: 'center',
              background: 'var(--surface-section)',
              borderTop: '1px solid var(--border-subtle)',
              color: 'var(--text-secondary)',
            }}
          >
            Ahora Virtual ©{new Date().getFullYear()}
          </Footer>
        </AdminLayoutPageHeadingContext.Provider>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
